import React, { useState, useEffect } from 'react';
import { api } from '../context/AppContext';
import { Button } from './ui/button';
import { Loader2, Mail, Bell, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const StatusBadge = ({ ok, label }) => (
  <div className="flex items-center gap-2">
    {ok ? (
      <CheckCircle className="w-4 h-4 text-green-500" />
    ) : (
      <XCircle className="w-4 h-4 text-red-500" />
    )}
    <span className={`text-sm ${ok ? 'text-green-400' : 'text-red-400'}`}>{label}</span>
  </div>
);

const CommTestPanel = ({ language }) => {
  const [infra, setInfra] = useState(null);
  const [loadingInfra, setLoadingInfra] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingPush, setSendingPush] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [pushResult, setPushResult] = useState(null);

  const fetchInfra = async () => {
    setLoadingInfra(true);
    try {
      const res = await api.get('/notifications/infrastructure-check');
      setInfra(res.data);
    } catch (e) {
      console.error('Infra check error:', e);
      toast.error('Erreur de diagnostic');
    } finally {
      setLoadingInfra(false);
    }
  };

  useEffect(() => { fetchInfra(); }, []);

  const handleTestEmail = async () => {
    setSendingEmail(true);
    setEmailResult(null);
    try {
      const res = await api.post('/notifications/test-email');
      setEmailResult(res.data);
      if (res.data.success) {
        toast.success(language === 'fr' ? 'Email de test envoye!' : 'Test email sent!');
      } else {
        toast.error(res.data.message || 'Echec');
      }
    } catch (e) {
      setEmailResult({ success: false, message: e.response?.data?.detail || 'Erreur' });
      toast.error('Erreur d\'envoi email');
    } finally {
      setSendingEmail(false);
      fetchInfra();
    }
  };

  const handleTestPush = async () => {
    setSendingPush(true);
    setPushResult(null);
    try {
      const res = await api.post('/notifications/test-push');
      setPushResult(res.data);
      if (res.data.success) {
        toast.success(language === 'fr' ? 'Notification push envoyee!' : 'Push notification sent!');
      } else {
        toast.error(res.data.message || 'Echec');
      }
    } catch (e) {
      setPushResult({ success: false, message: e.response?.data?.detail || 'Aucun abonnement push actif' });
      toast.error(language === 'fr' ? 'Échec — Activez les notifications dans votre navigateur d\'abord' : 'Failed — Enable browser notifications first');
    } finally {
      setSendingPush(false);
      fetchInfra();
    }
  };

  return (
    <div className="space-y-4">
      {/* Infrastructure Diagnostic */}
      <div className="p-4 bg-secondary/50 rounded-lg space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-heading text-sm uppercase tracking-wider text-muted-foreground">
            {language === 'fr' ? 'Diagnostic Infrastructure' : 'Infrastructure Diagnostic'}
          </p>
          <Button variant="ghost" size="sm" onClick={fetchInfra} disabled={loadingInfra} data-testid="infra-refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loadingInfra ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {infra ? (
          <div className="space-y-2">
            <StatusBadge ok={infra.email?.key_valid || infra.email?.configured} label={`Email (SendGrid) : ${infra.email?.status}`} />
            <StatusBadge ok={infra.web_push?.vapid_key_configured} label={`Web Push (VAPID) : ${infra.web_push?.status}`} />
            <div className="text-xs text-muted-foreground mt-2 space-y-1">
              <p>Abonnements push actifs (vous) : {infra.user_subscriptions?.your_active_subscriptions ?? 0}</p>
              <p>Total abonnements actifs : {infra.user_subscriptions?.total_active_subscriptions ?? 0}</p>
              <p>Membres avec push : {infra.user_subscriptions?.users_with_push_enabled ?? 0}</p>
            </div>
            <p className={`text-xs font-semibold mt-2 ${infra.email?.configured && infra.web_push?.vapid_key_configured ? 'text-green-400' : 'text-yellow-400'}`}>
              {infra.overall_status}
            </p>
          </div>
        ) : loadingInfra ? (
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        ) : null}
      </div>

      {/* Test Email Button */}
      <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
        <div>
          <p className="font-medium text-foreground flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            {language === 'fr' ? 'Test Email (SendGrid)' : 'Email Test (SendGrid)'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {language === 'fr' ? 'Envoie "TEST SYSTEME : Planification de la semaine" a votre adresse' : 'Sends test email to your address'}
          </p>
          {emailResult && (
            <div className={`mt-2 text-xs ${emailResult.success ? 'text-green-400' : 'text-red-400'}`}>
              {emailResult.success 
                ? `Statut ${emailResult.status_code} — ${emailResult.to_email}`
                : emailResult.message}
            </div>
          )}
        </div>
        <Button
          onClick={handleTestEmail}
          disabled={sendingEmail}
          className="bg-primary text-primary-foreground"
          data-testid="test-email-btn"
        >
          {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
          {language === 'fr' ? 'Envoyer' : 'Send'}
        </Button>
      </div>

      {/* Test Push Button */}
      <div className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
        <div>
          <p className="font-medium text-foreground flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" />
            {language === 'fr' ? 'Test Notification Push' : 'Push Notification Test'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            {language === 'fr' ? 'Envoie "Test Extra Miles" a vos appareils' : 'Sends test push to your devices'}
          </p>
          {pushResult && (
            <div className={`mt-2 text-xs ${pushResult.success ? 'text-green-400' : 'text-red-400'}`}>
              {pushResult.success 
                ? (language === 'fr' ? 'Notification delivree!' : 'Notification delivered!')
                : pushResult.message || (language === 'fr' ? 'Aucun abonnement push — activez les notifications navigateur d\'abord' : 'No push subscription — enable browser notifications first')}
            </div>
          )}
        </div>
        <Button
          onClick={handleTestPush}
          disabled={sendingPush}
          className="bg-primary text-primary-foreground"
          data-testid="test-push-btn"
        >
          {sendingPush ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
          {language === 'fr' ? 'Envoyer' : 'Send'}
        </Button>
      </div>

      {/* Recent Logs */}
      {infra?.recent_logs?.length > 0 && (
        <div className="p-4 bg-secondary/30 rounded-lg">
          <p className="font-heading text-xs uppercase tracking-wider text-muted-foreground mb-2">
            {language === 'fr' ? 'Derniers logs de livraison' : 'Recent delivery logs'}
          </p>
          <div className="space-y-1">
            {infra.recent_logs.map((log, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">
                  {log.type === 'test_email' ? 'Email' : 'Push'} — {new Date(log.timestamp).toLocaleString()}
                </span>
                {log.success ? (
                  <CheckCircle className="w-3 h-3 text-green-500" />
                ) : (
                  <XCircle className="w-3 h-3 text-red-500" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommTestPanel;
